'use client';

// Singleton market-lifecycle modal — mounted once at the AppShell root and
// driven by `useMarketLifecycleModalStore`. The page-scoped trigger
// (`useMarketLifecycleModalTrigger`) populates the store; this renders it above
// app chrome and survives navigation. Mirrors the `GlobalShareModal` pattern.
import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useAuth } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { type JSX, useEffect } from 'react';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { useNetworkMode } from '@/lib/network/use-network-mode';
import { logger } from '@/lib/telemetry/logger';
import { liveGraduationKey, useMarketLifecycleModalStore } from '@/stores/market-lifecycle-modal';
import { LifecycleModalView } from './lifecycle-modal-view';
import { lifecycleGlowClass } from './lifecycle-tone';
export function MarketLifecycleModal() {
  const $ = _c(25);
  const {
    user,
    isAuthenticated,
    isLoading
  } = useAuth();
  const {
    activeWallet
  } = useActiveWallet();
  const isDemoMode = useNetworkMode() === "devnet";
  const t = useTranslations("market.lifecycle_modal");
  const open = useMarketLifecycleModalStore(_temp);
  const payload = useMarketLifecycleModalStore(_temp2);
  const ownerKey = useMarketLifecycleModalStore(_temp3);
  const closeModal = useMarketLifecycleModalStore(_temp4);
  const viewerId = isAuthenticated ? user?.id ?? activeWallet?.address ?? "anonymous" : "anonymous";
  const t0 = payload?.marketPubkey ?? "";
  let t1;
  if ($[0] !== isDemoMode || $[1] !== t0 || $[2] !== viewerId) {
    t1 = liveGraduationKey(viewerId, t0, isDemoMode);
    $[0] = isDemoMode;
    $[1] = t0;
    $[2] = viewerId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const currentKey = t1;
  const belongsToViewer = ownerKey === null || ownerKey === currentKey;
  let t2;
  let t3;
  if ($[4] !== isDemoMode || $[5] !== isLoading || $[6] !== ownerKey || $[7] !== viewerId) {
    t2 = () => {
      if (isLoading || ownerKey === null) {
        return;
      }
      const current = useMarketLifecycleModalStore.getState();
      const expected = liveGraduationKey(viewerId, current.payload?.marketPubkey ?? "", isDemoMode);
      if (current.open && current.ownerKey !== null && current.ownerKey !== expected) {
        logger.info("market_lifecycle_modal.viewer_changed", {
          stage: current.payload?.stage
        });
        current.closeModal();
      }
    };
    t3 = [isLoading, viewerId, isDemoMode, ownerKey];
    $[4] = isDemoMode;
    $[5] = isLoading;
    $[6] = ownerKey;
    $[7] = viewerId;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  if (payload === null) {
    return null;
  }
  const t4 = open && !isLoading && belongsToViewer;
  let t5;
  if ($[10] !== closeModal) {
    t5 = next => {
      if (!next) {
        closeModal();
      }
    };
    $[10] = closeModal;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t) {
    t6 = t("a11yTitle");
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== payload.variant) {
    t7 = lifecycleGlowClass(payload.variant);
    $[14] = payload.variant;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== closeModal || $[17] !== payload) {
    t8 = <LifecycleModalView payload={payload} onClose={closeModal} />;
    $[16] = closeModal;
    $[17] = payload;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <SilkSheet variant="detached" size="md" open={t4} onOpenChange={t5} title={t6} srOnlyHeader={true} sheetClassName={t7} persistAcrossRoutes={true}>{t8}</SilkSheet>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
function _temp4(s_2) {
  return s_2.closeModal;
}
function _temp3(s_1) {
  return s_1.ownerKey;
}
function _temp2(s_0) {
  return s_0.payload;
}
function _temp(s) {
  return s.open;
}